import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getAwKolo, nastavCenuAwKolo, schvalitAwKolo, skrytAwKolo, type AwKoloDetail,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon } from "../lib/icons.js";

/**
 * Detail karty kola — to, co na eshopu vznikne jako JEDEN produkt s variantami
 * přední / zadní / sada × ráfek. Barvy náby, niplů a paprsků jsou VOLBY na
 * kartě (line item properties), ne varianty — 224 kombinací by Shopify
 * (strop 100 variant) neuneslo a zákazník by v tom nevybral nic.
 *
 * 💰 Každý cenový bod má cenu z konfigurátoru dodavatele; ruční přepis
 *    vyhrává (klik na cenu).
 */

const kc = (v: string | number | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

function Barvy({ nazev, barvy }: { nazev: string; barvy: Array<{ hex: string; nazev: string }> | undefined }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-24 text-zinc-500">{nazev}</span>
      <span className="flex gap-1.5 flex-wrap">
        {(barvy ?? []).map((b) => (
          <span key={b.hex} className="inline-flex items-center gap-1 text-xs text-zinc-700">
            <span className="inline-block h-4 w-4 rounded-full border border-zinc-300" style={{ backgroundColor: b.hex }} />
            {b.nazev}
          </span>
        ))}
        {(barvy ?? []).length === 0 && <span className="text-zinc-300 text-xs">—</span>}
      </span>
    </div>
  );
}

export function AngelswheelsKoloPage(): JSX.Element {
  const { key = "" } = useParams();
  const [d, setD] = useState<AwKoloDetail | null>(null);
  const [chyba, setChyba] = useState<string | null>(null);
  const [edit, setEdit] = useState<string | null>(null);
  const [hodnota, setHodnota] = useState("");
  const [pracuje, setPracuje] = useState(false);
  const [akceChyba, setAkceChyba] = useState<string | null>(null);

  const nacti = useCallback(async () => {
    try { setD(await getAwKolo(key)); } catch (e) { setChyba(e instanceof Error ? e.message : "nepovedlo se"); }
  }, [key]);
  useEffect(() => { void nacti(); }, [nacti]);

  if (chyba) return <EmptyState icon={<PackageIcon size={28} />} title="Nenalezeno" description={chyba} />;
  if (!d) return <LoadingBlock label="Načítám…" />;

  const akce = async (fn: () => Promise<void>): Promise<void> => {
    setPracuje(true); setAkceChyba(null);
    try { await fn(); await nacti(); } catch (e) { setAkceChyba(e instanceof Error ? e.message : "nepovedlo se"); } finally { setPracuje(false); }
  };
  const ulozCenu = async (sku: string): Promise<void> => {
    await akce(() => nastavCenuAwKolo(d.key, sku, hodnota.trim() === "" ? null : Number(hodnota)));
    setEdit(null);
  };

  return (
    <div className="p-6 max-w-5xl">
      <nav className="mb-4 text-sm text-zinc-500">
        <Link to="/angelswheels" className="hover:text-ktm-orange">Angel&apos;s Wheels</Link>
        <span className="mx-2 text-zinc-300">›</span><span>Kola</span>
        <span className="mx-2 text-zinc-300">›</span><span className="font-mono">{d.key}</span>
      </nav>

      <div className="flex items-start gap-3">
        <h1 className="text-xl font-semibold flex-1">{d.nazev}</h1>
        {d.stav === "na-eshopu" && <Badge tone="green">Na eshopu</Badge>}
        {d.stav === "ceka" && <Badge tone="blue">Čeká na robota</Badge>}
        {d.stav === "skryto" && <Badge tone="neutral">Skryto</Badge>}
        {d.stav === "chyba" && <Badge tone="red">Chyba</Badge>}
      </div>
      <div className="mt-1 text-sm text-zinc-500">
        {d.naboj === "kite" ? "Náboje Kite Elite" : "Náboje G2 ProSeries (výměnné adaptéry)"} · ráfky Excel · {[d.predni && `přední ${d.predni}`, d.zadni && `zadní ${d.zadni}`].filter(Boolean).join(" · ")} · {d.model}
      </div>

      <div className="mt-4 flex items-center gap-2">
        {d.stav === "na-eshopu" || d.stav === "ceka" ? (
          <Button variant="ghost" disabled={pracuje} onClick={() => void akce(() => skrytAwKolo(d.key))}>Skrýt z eshopu</Button>
        ) : (
          <Button disabled={pracuje} onClick={() => void akce(() => schvalitAwKolo(d.key))}>Pustit na eshop</Button>
        )}
        {d.shopifyHandle && (
          <a href={`https://ktm-vlasim.myshopify.com/products/${d.shopifyHandle}`} target="_blank" rel="noreferrer" className="text-sm text-zinc-500 hover:text-ktm-orange">karta na eshopu ↗</a>
        )}
      </div>
      {d.foundedError && <div className="mt-2 text-sm text-red-600">Robot hlásí: {d.foundedError}</div>}
      {akceChyba && <div className="mt-2 text-sm text-red-600">{akceChyba}</div>}

      <section className="mt-6">
        <h2 className="font-medium">Cenové body — budoucí varianty na eshopu</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Z konfigurátoru dodavatele, anonymně (přihlášený účet by dal −10 % = naši nákupku). Klik na cenu = ruční přepis.
        </p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full max-w-3xl text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-zinc-400">
                <th className="py-2 pr-3 text-left">Kolo</th><th className="py-2 pr-3 text-left">Ráfek</th>
                <th className="py-2 pr-3 text-left">SKU</th><th className="py-2 pr-3 text-right">Dodavatel</th>
                <th className="py-2 pr-3 text-right">Naše cena</th><th className="py-2 text-left">Pozn.</th>
              </tr>
            </thead>
            <tbody>
              {d.body.map((b) => (
                <tr key={b.sku} className={`border-t border-zinc-100 ${b.dostupne ? "" : "text-zinc-400"}`}>
                  <td className="py-1.5 pr-3">{b.koloNazev}</td>
                  <td className="py-1.5 pr-3">{b.rafek}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{b.sku}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{kc(b.cenaDodavatele)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {edit === b.sku ? (
                      <span className="inline-flex items-center gap-1">
                        <input type="number" value={hodnota} onChange={(e) => setHodnota(e.target.value)} className="w-24 rounded border border-slate-300 px-2 py-0.5 text-right" placeholder="prázdné = dodavatel" />
                        <Button size="sm" onClick={() => void ulozCenu(b.sku)} disabled={pracuje}>Ulož</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>×</Button>
                      </span>
                    ) : (
                      <button type="button" className="font-semibold hover:underline decoration-dotted" onClick={() => { setEdit(b.sku); setHodnota(b.naseCena ? String(Math.round(Number(b.naseCena))) : ""); }}>
                        {kc(b.naseCena)}{b.cenaRucne && <span className="ml-1 text-amber-600 text-xs">✎</span>}
                      </button>
                    )}
                  </td>
                  <td className="py-1.5 text-xs">
                    {!b.dostupne && "konfigurátor tuhle kombinaci nedává — na eshop nejde"}
                    {b.dostupne && b.historie.length > 1 && `cena se hnula ${b.historie.length - 1}×`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 space-y-1.5">
        <h2 className="font-medium">Volby na kartě (barvy) — nejsou varianty</h2>
        <Barvy nazev="Náboj" barvy={d.barvy.hub} />
        <Barvy nazev="Niple" barvy={d.barvy.nipple} />
        <Barvy nazev="Paprsky" barvy={d.barvy.spokes} />
        <p className="text-[11px] text-zinc-400">
          Barvy cenu nemění (změřeno 3. 9. 2026) — na eshopu jdou jako poznámka k položce v objednávce a obrázek se podle nich přepíná z renderů výrobce.
        </p>
      </section>
    </div>
  );
}
